import dayjs from 'dayjs';
import type { TFunction } from 'i18next';
import {
    CATALOG_IMPORT_CODE_KINDS,
    isCatalogImportCode,
    type CatalogImportCode,
} from '@/api/handlers/resources/catalogImports';
import type { CatalogImportLineStatus, CatalogImportMatch } from '@/api/models';
import { formatGel, tetriFromDecimal } from '@/utils/money';

/**
 * What the server said, as any of its shapes carries it: a row's problem or
 * warning, a names entry's problem, a header problem, or a refusal's
 * `{ code }` (an `ApiError` has no `params`).
 */
export type CatalogImportNoticeSource = {
    code: string;
    params?: Record<string, unknown>;
    fields?: readonly string[];
};

/**
 * One sentence, ready for `renderNotice`. `key` is `pages`-namespace-relative;
 * `params` are already text. `fields` are wire field names the sentence lists
 * as `{{fields}}`: they are translated at render, so they stay names here.
 */
export type CatalogImportNotice = {
    kind: 'localized';
    key: string;
    params: Record<string, string>;
    fields?: readonly string[];
};

export type CatalogImportNoticeTone = 'danger' | 'warning';

/** bos-api `CatalogImportRequestValidator.MaxLines`: its refusal carries no `params`. */
const SERVER_MAX_LINES = 2000;

const COUNT_FORMAT = new Intl.NumberFormat('en-US', {
    maximumFractionDigits: 0,
});

// ---- Params: the server's values as sentence text ------------------------

/** A name or value as given; a number as its digits; anything else blank. */
export const textParam = (value: unknown): string => {
    if (typeof value === 'string') {
        return value;
    }
    return typeof value === 'number' ? String(value) : '';
};

/** A list of names (axes, values), comma-joined in the server's order. */
export const listParam = (value: unknown): string =>
    Array.isArray(value) ? value.map(textParam).join(', ') : textParam(value);

/** Excel row numbers, comma-joined; never grouped (row 1003, not 1,003). */
export const rowsParam = (value: unknown): string => listParam(value);

/** A count with the grouping `formatGel` uses (`2,000`), whatever the language. */
export const countParam = (value: unknown): string =>
    typeof value === 'number' && Number.isFinite(value)
        ? COUNT_FORMAT.format(value)
        : textParam(value);

/** A decimal-GEL amount from the wire, as the app prints money (`₾ 1,234.50`). */
export const moneyParam = (value: unknown): string =>
    typeof value === 'number' && Number.isFinite(value)
        ? formatGel(tetriFromDecimal(value))
        : textParam(value);

/** A `YYYY-MM-DD` calendar date in the app's `DD/MM/YYYY`; no timezone involved. */
export const dateParam = (value: unknown): string =>
    typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)
        ? dayjs(value).format('DD/MM/YYYY')
        : textParam(value);

const isEmptyList = (value: unknown): boolean =>
    !Array.isArray(value) || value.length === 0;

/**
 * `NAME_MERGE_CYCLE`'s texts in the order they point: `A → B`. Not closed
 * back to the first: a text that only leads into the loop is listed too.
 */
const cycleParam = (value: unknown): string =>
    Array.isArray(value) ? value.map(textParam).join(' → ') : textParam(value);

// ---- Sentences --------------------------------------------------------------

type NoticeBuilder = (
    params: Record<string, unknown>,
    fields: readonly string[],
) => CatalogImportNotice;

/**
 * The fields a sentence lists: the problem's `fields`, which the server fills
 * with the same names as a field-list param, else that param.
 */
const fieldsOf = (
    fields: readonly string[],
    listed: unknown,
): readonly string[] =>
    fields.length > 0 || !Array.isArray(listed)
        ? fields
        : listed.filter((field): field is string => typeof field === 'string');

/** `DATE_INVALID`'s one field: the problem's `fields`, else its `field` param. */
const dateFieldsOf = (
    fields: readonly string[],
    field: unknown,
): readonly string[] =>
    fields.length > 0 || typeof field !== 'string' ? fields : [field];

/** Fields whose amounts are money; the rest of `AMOUNT_NEGATIVE`'s are counts. */
const MONEY_FIELDS: ReadonlySet<string> = new Set([
    'cost',
    'salePrice',
    'wholesalePrice',
]);

/**
 * One sentence per code, keyed `catalogImport.notices.<CODE>`. A code whose
 * params change the sentence's shape picks a variant key,
 * `catalogImport.notices.<CODE>_<variant>`.
 *
 * Every key is written out as a literal beside `kind: 'localized'` on
 * purpose: `localizedNoticeKeys.test.ts` finds keys by reading the source, so
 * a key assembled from the code would go unchecked.
 */
const NOTICE_BUILDERS = {
    // ---- Header: the request as a whole -----------------------------------
    IMPORT_TOO_MANY_LINES: (params) => ({
        kind: 'localized',
        key: 'catalogImport.notices.IMPORT_TOO_MANY_LINES',
        params: { max: countParam(params.max ?? SERVER_MAX_LINES) },
    }),
    IMPORT_TOO_MANY_UNITS: (params) => ({
        kind: 'localized',
        key: 'catalogImport.notices.IMPORT_TOO_MANY_UNITS',
        params: {
            units: countParam(params.units),
            max: countParam(params.max),
        },
    }),
    IMPORT_LINES_INVALID: () => ({
        kind: 'localized',
        key: 'catalogImport.notices.IMPORT_LINES_INVALID',
        params: {},
    }),
    IMPORT_REQUEST_INVALID: () => ({
        kind: 'localized',
        key: 'catalogImport.notices.IMPORT_REQUEST_INVALID',
        params: {},
    }),
    BRANCH_UNKNOWN: () => ({
        kind: 'localized',
        key: 'catalogImport.notices.BRANCH_UNKNOWN',
        params: {},
    }),
    BRANCH_ARCHIVED: (params) => ({
        kind: 'localized',
        key: 'catalogImport.notices.BRANCH_ARCHIVED',
        params: { branch: textParam(params.branchName) },
    }),
    BRANCH_NOT_ALLOWED: () => ({
        kind: 'localized',
        key: 'catalogImport.notices.BRANCH_NOT_ALLOWED',
        params: {},
    }),
    // `reasons` are the ledger's own codes; the sentence names what to do.
    ACCOUNTING_NOT_READY: (params) => ({
        kind: 'localized',
        key: 'catalogImport.notices.ACCOUNTING_NOT_READY',
        params: { date: dateParam(params.date) },
    }),
    IMPORT_NOT_FOUND: () => ({
        kind: 'localized',
        key: 'catalogImport.notices.IMPORT_NOT_FOUND',
        params: {},
    }),
    IMPORT_CONFLICT: () => ({
        kind: 'localized',
        key: 'catalogImport.notices.IMPORT_CONFLICT',
        params: {},
    }),

    // ---- Header: undo refusals ---------------------------------------------
    ONBOARDING_FINISHED: () => ({
        kind: 'localized',
        key: 'catalogImport.notices.ONBOARDING_FINISHED',
        params: {},
    }),
    IMPORT_NOT_LATEST: () => ({
        kind: 'localized',
        key: 'catalogImport.notices.IMPORT_NOT_LATEST',
        params: {},
    }),
    IMPORT_ALREADY_UNDONE: () => ({
        kind: 'localized',
        key: 'catalogImport.notices.IMPORT_ALREADY_UNDONE',
        params: {},
    }),
    IMPORT_CONSUMED: () => ({
        kind: 'localized',
        key: 'catalogImport.notices.IMPORT_CONSUMED',
        params: {},
    }),

    // ---- Problems: names ----------------------------------------------------
    // A names entry carries `text` (and `name` when a rename collides); a row
    // whose cell points at that entry carries the same `text`.
    BRAND_AMBIGUOUS: (params) => ({
        kind: 'localized',
        key: 'catalogImport.notices.BRAND_AMBIGUOUS',
        params: { name: textParam(params.name ?? params.text) },
    }),
    CATEGORY_AMBIGUOUS: (params) => ({
        kind: 'localized',
        key: 'catalogImport.notices.CATEGORY_AMBIGUOUS',
        params: { name: textParam(params.name ?? params.text) },
    }),
    SUPPLIER_AMBIGUOUS: (params) => ({
        kind: 'localized',
        key: 'catalogImport.notices.SUPPLIER_AMBIGUOUS',
        params: { name: textParam(params.name ?? params.text) },
    }),
    // With a `text`: a names decision (or the row's cell through it) points at
    // nothing. Without one: the row's own `categoryId` / `brandId` /
    // `supplierId` names nothing, and `fields` says which.
    NAME_TARGET_UNKNOWN: (params, fields): CatalogImportNotice =>
        params.text === undefined
            ? {
                  kind: 'localized',
                  key: 'catalogImport.notices.NAME_TARGET_UNKNOWN_field',
                  params: {},
                  fields,
              }
            : {
                  kind: 'localized',
                  key: 'catalogImport.notices.NAME_TARGET_UNKNOWN',
                  params: { text: textParam(params.text) },
              },
    NAME_MERGE_CYCLE: (params) => ({
        kind: 'localized',
        key: 'catalogImport.notices.NAME_MERGE_CYCLE',
        params: { cycle: cycleParam(params.cycle) },
    }),

    // ---- Problems: matching -------------------------------------------------
    AXES_DIFFER_FROM_PRODUCT: (params): CatalogImportNotice =>
        isEmptyList(params.lineAxes)
            ? {
                  kind: 'localized',
                  key: 'catalogImport.notices.AXES_DIFFER_FROM_PRODUCT_noAxes',
                  params: {
                      sku: textParam(params.sku),
                      product: textParam(params.product),
                      productAxes: listParam(params.productAxes),
                  },
              }
            : {
                  kind: 'localized',
                  key: 'catalogImport.notices.AXES_DIFFER_FROM_PRODUCT',
                  params: {
                      sku: textParam(params.sku),
                      product: textParam(params.product),
                      productAxes: listParam(params.productAxes),
                      lineAxes: listParam(params.lineAxes),
                  },
              },
    PRODUCT_IS_SIMPLE: (params) => ({
        kind: 'localized',
        key: 'catalogImport.notices.PRODUCT_IS_SIMPLE',
        params: {
            sku: textParam(params.sku),
            product: textParam(params.product),
        },
    }),
    BARCODE_AMBIGUOUS: (params) => ({
        kind: 'localized',
        key: 'catalogImport.notices.BARCODE_AMBIGUOUS',
        params: { barcode: textParam(params.barcode) },
    }),
    SKU_BARCODE_CONFLICT: (params) => ({
        kind: 'localized',
        key: 'catalogImport.notices.SKU_BARCODE_CONFLICT',
        params: {
            sku: textParam(params.sku),
            skuProduct: textParam(params.skuProduct),
            barcodeProduct: textParam(params.barcodeProduct),
        },
    }),
    // With a `sku`: the SKU's simple product has no variant to restock (a
    // catalogue fault). Otherwise the line's own `variantId` names nothing.
    VARIANT_UNKNOWN: (params): CatalogImportNotice =>
        params.sku === undefined
            ? {
                  kind: 'localized',
                  key: 'catalogImport.notices.VARIANT_UNKNOWN',
                  params: {},
              }
            : {
                  kind: 'localized',
                  key: 'catalogImport.notices.VARIANT_UNKNOWN_sku',
                  params: { sku: textParam(params.sku) },
              },

    // ---- Problems: groups ---------------------------------------------------
    // On every row of the group, each listing all of its rows.
    GROUP_FIELDS_DIFFER: (params, fields) => ({
        kind: 'localized',
        key: 'catalogImport.notices.GROUP_FIELDS_DIFFER',
        params: { sku: textParam(params.sku), rows: rowsParam(params.rows) },
        fields: fieldsOf(fields, params.fields),
    }),
    // Kept in the server's contract but no longer raised: a row missing an
    // axis its siblings have takes "Unspecified" instead.
    GROUP_AXES_DIFFER: (params) => ({
        kind: 'localized',
        key: 'catalogImport.notices.GROUP_AXES_DIFFER',
        params: {
            sku: textParam(params.sku),
            rows: rowsParam(params.rows),
            axes: listParam(params.axes),
        },
    }),
    BARCODE_SHARED_ACROSS_SKUS: (params) => ({
        kind: 'localized',
        key: 'catalogImport.notices.BARCODE_SHARED_ACROSS_SKUS',
        params: {
            barcode: textParam(params.barcode),
            skus: listParam(params.skus),
            rows: rowsParam(params.rows),
        },
    }),
    VARIANT_FIELDS_DIFFER: (params, fields) => ({
        kind: 'localized',
        key: 'catalogImport.notices.VARIANT_FIELDS_DIFFER',
        params: { rows: rowsParam(params.rows) },
        fields: fieldsOf(fields, params.fields),
    }),
    BARCODE_LOOKS_LIKE_IMEI: (params) => ({
        kind: 'localized',
        key: 'catalogImport.notices.BARCODE_LOOKS_LIKE_IMEI',
        params: { barcode: textParam(params.barcode) },
    }),

    // ---- Problems: axes and values ------------------------------------------
    AXIS_NOT_ON_CATEGORY: (params) => ({
        kind: 'localized',
        key: 'catalogImport.notices.AXIS_NOT_ON_CATEGORY',
        params: {
            attributeType: textParam(params.attributeType),
            category: textParam(params.category),
        },
    }),
    CATEGORY_REQUIRED_FOR_OPTIONS: () => ({
        kind: 'localized',
        key: 'catalogImport.notices.CATEGORY_REQUIRED_FOR_OPTIONS',
        params: {},
    }),
    ATTRIBUTE_TYPE_UNKNOWN: (params) => ({
        kind: 'localized',
        key: 'catalogImport.notices.ATTRIBUTE_TYPE_UNKNOWN',
        params: { attributeType: textParam(params.attributeType) },
    }),
    ATTRIBUTE_VALUE_UNKNOWN: (params) => ({
        kind: 'localized',
        key: 'catalogImport.notices.ATTRIBUTE_VALUE_UNKNOWN',
        params: { attributeType: textParam(params.attributeType) },
    }),

    // ---- Problems: the row's own values -------------------------------------
    SKU_REQUIRED: () => ({
        kind: 'localized',
        key: 'catalogImport.notices.SKU_REQUIRED',
        params: {},
    }),
    NAME_REQUIRED: () => ({
        kind: 'localized',
        key: 'catalogImport.notices.NAME_REQUIRED',
        params: {},
    }),
    SALE_PRICE_REQUIRED: () => ({
        kind: 'localized',
        key: 'catalogImport.notices.SALE_PRICE_REQUIRED',
        params: {},
    }),
    VALUE_TOO_LONG: (params, fields) => ({
        kind: 'localized',
        key: 'catalogImport.notices.VALUE_TOO_LONG',
        params: {
            length: countParam(params.length),
            max: countParam(params.max),
        },
        fields,
    }),
    QUANTITY_REQUIRED: () => ({
        kind: 'localized',
        key: 'catalogImport.notices.QUANTITY_REQUIRED',
        params: {},
    }),
    // Also a negative quantity or one past what stock can count: the sentence
    // states the rule rather than "not whole".
    QUANTITY_NOT_WHOLE: (params, fields) => ({
        kind: 'localized',
        key: 'catalogImport.notices.QUANTITY_NOT_WHOLE',
        params: { value: textParam(params.value) },
        fields,
    }),
    COST_REQUIRED: (params) => ({
        kind: 'localized',
        key: 'catalogImport.notices.COST_REQUIRED',
        params: { quantity: countParam(params.quantity) },
    }),
    AMOUNT_NEGATIVE: (params, fields) => ({
        kind: 'localized',
        key: 'catalogImport.notices.AMOUNT_NEGATIVE',
        params: {
            value: fields.some((field) => MONEY_FIELDS.has(field))
                ? moneyParam(params.value)
                : textParam(params.value),
        },
        fields,
    }),
    // A warning since API-195: `used` is the date the line goes on with
    // (today for a purchase date), or null when the date is dropped.
    DATE_INVALID: (params, fields): CatalogImportNotice =>
        typeof params.used === 'string' && params.used !== ''
            ? {
                  kind: 'localized',
                  key: 'catalogImport.notices.DATE_INVALID',
                  params: {
                      value: textParam(params.value),
                      used: dateParam(params.used),
                  },
                  fields: dateFieldsOf(fields, params.field),
              }
            : {
                  kind: 'localized',
                  key: 'catalogImport.notices.DATE_INVALID_dropped',
                  params: { value: textParam(params.value) },
                  fields: dateFieldsOf(fields, params.field),
              },
    VAT_TYPE_UNKNOWN: (params) => ({
        kind: 'localized',
        key: 'catalogImport.notices.VAT_TYPE_UNKNOWN',
        params: { value: textParam(params.value) },
    }),
    UNIT_UNKNOWN: (params) => ({
        kind: 'localized',
        key: 'catalogImport.notices.UNIT_UNKNOWN',
        params: { value: textParam(params.value) },
    }),

    // ---- Warnings -------------------------------------------------------------
    AXIS_VALUE_UNSPECIFIED: (params) => ({
        kind: 'localized',
        key: 'catalogImport.notices.AXIS_VALUE_UNSPECIFIED',
        params: { attributeType: textParam(params.attributeType) },
    }),
    BARCODE_FROM_SKU: (params) => ({
        kind: 'localized',
        key: 'catalogImport.notices.BARCODE_FROM_SKU',
        params: { sku: textParam(params.sku) },
    }),
    VALUE_WILL_BE_CREATED: (params) => ({
        kind: 'localized',
        key: 'catalogImport.notices.VALUE_WILL_BE_CREATED',
        params: {
            attributeType: textParam(params.attributeType),
            value: textParam(params.value),
        },
    }),
    SALE_BELOW_COST: (params) => ({
        kind: 'localized',
        key: 'catalogImport.notices.SALE_BELOW_COST',
        params: {
            salePrice: moneyParam(params.salePrice),
            cost: moneyParam(params.cost),
        },
    }),
    DATE_CAPPED_TO_TODAY: (params) => ({
        kind: 'localized',
        key: 'catalogImport.notices.DATE_CAPPED_TO_TODAY',
        params: {
            date: dateParam(params.date),
            today: dateParam(params.today),
        },
    }),
    CATALOGUE_KEPT: (params, fields) => ({
        kind: 'localized',
        key: 'catalogImport.notices.CATALOGUE_KEPT',
        params: { product: textParam(params.product) },
        fields: fieldsOf(fields, params.fields),
    }),
    PRICE_DIFFERS_FROM_CATALOGUE: (params) => ({
        kind: 'localized',
        key: 'catalogImport.notices.PRICE_DIFFERS_FROM_CATALOGUE',
        params: {
            catalogPrice: moneyParam(params.cataloguePrice),
            filePrice: moneyParam(params.filePrice),
        },
    }),
    BARCODES_KEPT_AS_ALTERNATES: (params) => ({
        kind: 'localized',
        key: 'catalogImport.notices.BARCODES_KEPT_AS_ALTERNATES',
        params: {
            primary: textParam(params.primary),
            alternates: listParam(params.alternates),
            rows: rowsParam(params.rows),
        },
    }),
    BARCODE_ADDED_AS_ALTERNATE: (params) => ({
        kind: 'localized',
        key: 'catalogImport.notices.BARCODE_ADDED_AS_ALTERNATE',
        params: { barcode: textParam(params.barcode) },
    }),
    // `fields` names the figures that differ from the main barcode's line.
    PRICE_FROM_PRIMARY_BARCODE: (params, fields) => ({
        kind: 'localized',
        key: 'catalogImport.notices.PRICE_FROM_PRIMARY_BARCODE',
        params: {
            primary: textParam(params.primary),
            row: rowsParam(params.row),
        },
        fields,
    }),
} satisfies Record<CatalogImportCode, NoticeBuilder>;

/**
 * The sentence for a code this app cannot describe, one the server added
 * after this build. Names the code rather than show the server's English.
 */
const unknownCodeNotice = (code: string): CatalogImportNotice => ({
    kind: 'localized',
    key: 'catalogImport.notices.unknownCode',
    params: { code },
});

/**
 * A row problem the app raises itself, never the server: a number cell the
 * parse could not read (`12,5x`, `n/a`, `₾45`), which the line leaves off
 * because the API takes numbers only. `fields` names the field and
 * `params.value` the text as the file has it. Not one of
 * `CatalogImportCode`, whose list mirrors the server's.
 */
export const CELL_NOT_A_NUMBER = 'CELL_NOT_A_NUMBER';

const cellNotANumberNotice: NoticeBuilder = (params, fields) => ({
    kind: 'localized',
    key: 'catalogImport.notices.CELL_NOT_A_NUMBER',
    params: { value: textParam(params.value) },
    fields,
});

/** The notice for one problem, warning or refusal of the catalog import. */
export const noticeFor = ({
    code,
    params,
    fields,
}: CatalogImportNoticeSource): CatalogImportNotice => {
    if (code === CELL_NOT_A_NUMBER) {
        return cellNotANumberNotice(params ?? {}, fields ?? []);
    }
    return isCatalogImportCode(code)
        ? NOTICE_BUILDERS[code](params ?? {}, fields ?? [])
        : unknownCodeNotice(code);
};

/**
 * Warnings in warning tone; problems, header refusals and codes this app does
 * not know in danger tone.
 */
export const toneFor = (code: string): CatalogImportNoticeTone =>
    isCatalogImportCode(code) && CATALOG_IMPORT_CODE_KINDS[code] === 'warning'
        ? 'warning'
        : 'danger';

// ---- Labels the notes, chips and drawer share --------------------------------

/** Every field name the server's `fields` and field-list params use, with its label. */
export const FIELD_LABEL_KEYS = {
    sku: 'catalogImport.fields.sku',
    barcode: 'catalogImport.fields.barcode',
    name: 'catalogImport.fields.name',
    category: 'catalogImport.fields.category',
    brand: 'catalogImport.fields.brand',
    supplier: 'catalogImport.fields.supplier',
    unit: 'catalogImport.fields.unit',
    vatType: 'catalogImport.fields.vatType',
    optionValues: 'catalogImport.fields.optionValues',
    quantity: 'catalogImport.fields.quantity',
    cost: 'catalogImport.fields.cost',
    salePrice: 'catalogImport.fields.salePrice',
    wholesalePrice: 'catalogImport.fields.wholesalePrice',
    minimumQuantity: 'catalogImport.fields.minimumQuantity',
    purchaseDate: 'catalogImport.fields.purchaseDate',
    expiryDate: 'catalogImport.fields.expiryDate',
    saleDueDate: 'catalogImport.fields.saleDueDate',
    variantId: 'catalogImport.fields.variantId',
    lotNumber: 'catalogImport.fields.lotNumber',
} as const;

export type CatalogImportField = keyof typeof FIELD_LABEL_KEYS;

export const LINE_STATUS_LABEL_KEYS = {
    ready: 'catalogImport.status.ready',
    needsAttention: 'catalogImport.status.needsAttention',
    excluded: 'catalogImport.status.excluded',
    draft: 'catalogImport.status.draft',
} as const satisfies Record<CatalogImportLineStatus, string>;

export const MATCH_LABEL_KEYS = {
    newProduct: 'catalogImport.match.newProduct',
    newVariant: 'catalogImport.match.newVariant',
    restock: 'catalogImport.match.restock',
} as const satisfies Record<CatalogImportMatch, string>;

const isCatalogImportField = (field: string): field is CatalogImportField =>
    Object.hasOwn(FIELD_LABEL_KEYS, field);

/**
 * The sentence, in the language `t` speaks. Resolves under `pages` whatever
 * namespace `t` was bound to. A field the app has no label for is shown by
 * its wire name rather than as a raw key.
 */
export const renderNotice = (
    t: TFunction,
    notice: CatalogImportNotice,
): string => {
    const fields =
        notice.fields === undefined
            ? {}
            : {
                  fields: notice.fields
                      .map((field) =>
                          isCatalogImportField(field)
                              ? t(FIELD_LABEL_KEYS[field], { ns: 'pages' })
                              : field,
                      )
                      .join(', '),
              };

    return t(notice.key, { ns: 'pages', ...notice.params, ...fields });
};
